/**
 * Echo Hunt -- the initial world pool, the frame, and the character generator (P8, §8.2 + §8.5).
 *
 * PURE module: no React, no `store`, no DB imports. The worlds are seeded into
 * `echoProfiles.worldPool` (a JSON column) by the `*_Live.ts` wrapper; this module only owns the
 * data and the pure selection/creation logic.
 *
 * ## Why these worlds (§8.5)
 *
 * The frame (§8.2) already makes bizarreness canonical -- the character believes impossible things
 * are simply happening in a real situation -- but a world that CANNOT absorb a wild event forces the
 * weaver to smooth it into something ordinary, and smoothing is exactly how a real dream bullet gets
 * eroded (§17 "real bullets eroded by reshape / weave / rewrite"). So the pool favours worlds with a
 * very high tolerance for absurdity, and the tolerance is a field on each world so the weaver can be
 * told about it rather than having to guess.
 *
 * Two exclusions, both deliberate:
 *  - **No worlds that are ABOUT dreams** (Sandman, Paprika, Inception). They would blur the label
 *    in-story: a character walking through a dream-world is not obviously wrong to think they are
 *    awake, and the user's own job is to notice that exact kind of wrongness (§8.5).
 *  - **No world the user has no feel for.** All of these are widely known, so the user can tell when a
 *    woven event violates the world rather than the dream.
 *
 * Bibles are short (well under the §8.5 300-word cap) and written as CONSTRAINTS the weaver can obey:
 * what kinds of place exist, who lives there, what is impossible, and what is unremarkable. They are
 * editable on the Settings tab, and `generated: false` marks them as canonical rather than invented.
 *
 * ## Characters (§8.2)
 *
 * "Characters with strong values (cautious vs. opportunistic), since acting true to personality is
 * what carries the reading interest." A character is therefore a NAME, a disposition on the
 * cautious<->opportunistic axis, and a few notes the weaver must honour. `CreateCharacter` is
 * deterministic given an rng, so a seeded run is reproducible -- and it composes the notes FROM the
 * disposition, so a "very cautious" character never gets notes describing impulsive behaviour.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.2, §8.5, §16.2 P8.
 */
import type {EchoCharacter, EchoWorld} from "./Types.js";
import {GenerateSlotId} from "./Reshape.js";

// the frame (§8.2)
// ==========

/** The one frame v1 ships with (§8.2). Kept as an id rather than as prose so it can be logged on the
 * round (`EchoRound.frame`) and so a second frame can be added later without migrating rows. */
export const FRAME_DREAMING_BUT_AWAKE = "dreamingButAwake";

export const FRAME_DESCRIPTIONS: Record<string, string> = {
	[FRAME_DREAMING_BUT_AWAKE]:
		"The character is dreaming and believes they are awake. They have whatever abilities this world "
		+ "allows them, and they act on their ordinary values applied to an extraordinary situation.",
};

/** The world pool's tolerance field is 0..1, and `Prompts/Weave.ts:DescribeCrazinessTolerance` is the
 * single place those numbers become prose -- so the Settings tab and the prompt cannot describe the same
 * number differently. (Removed an earlier duplicate label table here for exactly that reason.) */

// the seeded worlds (§8.5)
// ==========

/** The worlds a story can be dropped into, seeded into a new profile's `worldPool`.
 *
 * Ids are STABLE SLUGS, not random ids, so re-seeding (or a user who deletes a world and reloads) is
 * idempotent: two copies of "Wonderland" cannot end up in one pool under different ids. A generated
 * world gets a random id instead (`GenerateWorldId`), since there is nothing to stay stable against.
 *
 * Each bible states: what the place is, what is ordinary there, what is impossible there, and the one
 * or two facts the weaver must not contradict. It deliberately does NOT summarise a plot -- a plot
 * summary invites the weaver to write scenes from it instead of from the bullets. */
export const SEED_WORLDS: EchoWorld[] = [
	{
		id: "world_wonderland",
		name: "Wonderland",
		crazinessTolerance: .98,
		generated: false,
		bible: [
			"A country reached by falling, where the rules of size, distance and logic are administrative",
			"decisions rather than physics. Rooms change what they are for. Signs mean what they say and",
			"nothing they imply. Nothing here is surprising to the people who live in it: a talking animal",
			"complaining about its schedule is ordinary, and anyone treating it as strange is the one being",
			"rude. Authority is held by whoever is loudest and best dressed, and it is enforced on a whim.",
			"Bureaucratic catch-22s are real obstacles: a rule cannot be broken, only found to have said",
			"something else all along. Meals, tea and executions are scheduled together. IMPORTANT: the place",
			"is not menacing and not whimsical -- it is matter-of-fact. Events do not resolve into a moral,",
			"and a puzzle here may simply stay unsolved.",
		].join(" "),
	},
	{
		id: "world_discworld",
		name: "The Disc",
		crazinessTolerance: .9,
		generated: false,
		bible: [
			"A flat world carried on the backs of four elephants standing on a great turtle, lit by a small",
			"sun that orbits close overhead. Cities are crowded, wet and bureaucratic; magic is real and",
			"expensive and usually not worth it. The dead persist as a matter of administration, and the",
			"local deities exist, are petty, and mostly attend to their own affairs. Trolls, dwarfs, the",
			"undead and the occasional wizard hold jobs, argue about wages and file complaints. Ordinary life",
			"is grubby and practical: ale, forms, guild disputes, a dripping ceiling. IMPORTANT: absurd",
			"premises get treated as dull problems to be managed, never as marvels. Nothing is explained by",
			"destiny. Awe is reserved for very little, and sarcasm is the standard register.",
		].join(" "),
	},
	{
		id: "world_spiritedAway",
		name: "The Bathhouse World",
		crazinessTolerance: .85,
		generated: false,
		bible: [
			"A world running alongside the ordinary one, entered through the back of a building or under an",
			"arch at dusk. It is dominated by a huge bathhouse where spirits come to be cleaned, and by the",
			"narrow lanes of a town that only exists at night. Names and identities can be taken and held by",
			"other people; forgetting a name is a real and permanent danger. Food and gold are dangerous",
			"because they are satisfying. Shapes shift: a creature may be enormous and then small, a person",
			"may be an animal and not mind. Work is the normal state of affairs -- contracts, chores,",
			"seniority. IMPORTANT: the place is beautiful and indifferent rather than evil. Rules are absolute",
			"once stated, and politeness has real force. Wonder is available, but so is dirt, exhaustion and",
			"smell.",
		].join(" "),
	},
	{
		id: "world_gravityFalls",
		name: "The Pine Town",
		crazinessTolerance: .85,
		generated: false,
		bible: [
			"A small forest town where the woods contain things that are not permitted: gnomes obsessed with",
			"a specific grievance, a lake with a thing in it, cabins whose interiors are larger and stranger",
			"than their outsides, machines left running by people who are no longer there. The town itself",
			"insists nothing is unusual; the local paper prints the anomalies as filler. Conspiracies are",
			"real and mostly small. Adults dismiss what children notice, and the dismissal is the standing",
			"joke as well as the standing danger. IMPORTANT: the tone is deadpan and genuinely funny, and",
			"the strangeness is local rather than cosmic. Nobody makes a speech about meaning. Injuries,",
			"boredom, summer jobs and bad food are as present as the impossible.",
		].join(" "),
	},
	{
		id: "world_tollbooth",
		name: "The Tollbooth Lands",
		crazinessTolerance: .95,
		generated: false,
		bible: [
			"A territory reached through a painted map, where the map is a fixed fact and the land is a",
			"weakly-committed rumour of it. Places exist because they are named and are reached by the route",
			"the name implies: the Doldrums, the Mountains of Ignorance, the city of Illusions, a market that",
			"sells words by the dozen. Languages, numbers and colours are physical commodities here. Bureaucracy",
			"is literal -- a demon watches every road and the only way past is an argument. IMPORTANT: nothing",
			"is decorated for the visitor's benefit; the absurdity is structural and completely unsentimental.",
			"An event may end without teaching anything, and the character may simply be tired afterwards.",
		].join(" "),
	},
	{
		id: "world_nightVale",
		name: "The Desert Town",
		crazinessTolerance: .92,
		generated: false,
		bible: [
			"A desert town under permanent community radio. There is a dog park that must not be entered, a",
			"glowing cloud that rains dead animals in a specific season, a faceless entity that is a valued",
			"member of the community, and a municipal government that is friendly, competent and openly",
			"incomprehensible. Residents accept every anomaly as a normal inconvenience to be scheduled",
			"around, and are mildly annoyed rather than frightened by it. IMPORTANT: the register is",
			"deadpan civic announcement. Weather, zoning, road closures and school boards matter. Nobody",
			"remarks that anything is impossible; the impossible is handled by policy.",
		].join(" "),
	},
];

/** How many seed worlds a fresh pool gets. All six -- they are the point of the pool (§8.5), and a
 * round cannot change worlds without at least two. */
export function GetSeedWorlds(): EchoWorld[] {
	// deep copies, so a caller editing a bible cannot corrupt the module's constant
	return SEED_WORLDS.map(world=>({...world}));
}

/** A random id for a generated original world (§8.5 `[EXPERIMENT]`). Reuses the slot-id helper so the
 * pure module stays free of `mobx-graphlink` (see `Reshape.ts:GenerateSlotId` for why). */
export function GenerateWorldId(): string {
	return `world_gen_${GenerateSlotId()}`;
}

/** Picks the world for a new story, optionally excluding the current one (§8.3 "going through a door
 * picks a new world from the pool, excluding the current one").
 *
 * Falls back to including the excluded world when it is the ONLY one -- a story whose door leads
 * straight back into the same world is a smaller failure than a door that leads nowhere, and a pool
 * with one entry is a setup problem the Settings tab shows rather than one to crash on. */
export function PickRandomWorld(pool: EchoWorld[]|n, rng: () => number, excludeWorldId?: string|n): EchoWorld|null {
	const worlds = (pool ?? []).filter(a=>a != null);
	if (worlds.length == 0) return null;
	if (worlds.length == 1) return worlds[0];

	const candidates = excludeWorldId == null ? worlds : worlds.filter(a=>a.id != excludeWorldId);
	const usable = candidates.length > 0 ? candidates : worlds;
	const index = Math.min(usable.length - 1, Math.max(0, Math.floor(rng() * usable.length)));
	return usable[index];
}

// schematic rules (§8.1)
// ==========

/** Starting in-dream schematic rules (§8.1, invariant #8). These are the user's OWN regularities that
 * the weave must not break -- each one is something that is true in their dreams, so a story that
 * violates it teaches the wrong schema. Seeded only when the profile has none, and freely editable:
 * an empty list simply means the weaver is told no extra rules.
 *
 * Kept short and phrased as constraints rather than as preferences, because the weaver receives them as
 * hard rules. */
export const DEFAULT_SCHEMATIC_RULES: string[] = [
	"Travel goes to objective, persistent places: a place you go to is somewhere that continues to exist, not a constructed imitation of somewhere.",
	"Nobody edits the world directly as a way of getting somewhere. Movement happens by moving -- walking, vehicles, doors, flight.",
	"Abilities belong to the character, not to objects they happen to be holding.",
];

// characters (§8.2)
// ==========

/** Given names for generated characters. Deliberately plain and varied: an exotic name is a small
 * attention magnet inside a story whose whole point is that nothing in the prose should signal class,
 * and it also makes the character harder to merge into an existing world. */
export const CHARACTER_NAMES = [
	"Marisa", "Tomas", "Ines", "Dov", "Priya", "Nikolai", "Adaeze", "Beatriz", "Hiroshi", "Maeve",
	"Rafael", "Selin", "Oskar", "Junie", "Halima", "Ferran", "Wren", "Casimir", "Anja", "Teodoro",
	"Ruby", "Emeka", "Saoirse", "Ilya", "Nadja", "Quentin", "Paloma", "Emil", "Leilani", "Bruno",
] as const;

/** What a character was doing when the story opened. This is the character's ORDINARY competence --
 * §8.2 wants someone whose values visibly drive what they do with impossible abilities, so the notes
 * are written as habits of a person with a trade, not as heroic traits. */
export const CHARACTER_ROLES = [
	{role: "night porter", habit: "keeps a mental inventory of every room they walk through"},
	{role: "courier", habit: "reads a route before entering a place, and dislikes backtracking"},
	{role: "schoolteacher", habit: "explains things to people who did not ask"},
	{role: "mechanic", habit: "takes things apart to see how they work, then forgets to reassemble them"},
	{role: "nurse", habit: "checks whether a person is hurt before asking anything else"},
	{role: "shopkeeper", habit: "counts what is in front of them, and notices what is missing"},
	{role: "cartographer", habit: "cannot leave a place without knowing its shape"},
	{role: "cook", habit: "evaluates every unfamiliar thing by whether it can be eaten and how it should be cooked"},
	{role: "dog walker", habit: "watches animals for what they have already noticed"},
	{role: "night-shift security guard", habit: "trusts doors and corners more than people"},
	{role: "musician", habit: "listens to a room before looking at it"},
	{role: "beekeeper", habit: "moves slowly and never swats at anything"},
] as const;

/** Notes that follow FROM the disposition, so a cautious character never gets impulsive notes. Each
 * entry is (text, weight-range) on the -1..+1 axis.
 *
 * As of §8.7 this table has TWO jobs: the offline rng fallback (`CreateCharacter`) and the REGISTER
 * EXAMPLES handed to the lore generator, which is what keeps a drafted character reading like the rest
 * of the pool rather than like generic character prose. Exported for that reason. */
export const CHARACTER_DISPOSITION_NOTES = [
	{max: -.55, notes: [
		"Will not step through an unfamiliar opening without knowing what is on the other side.",
		"Would rather leave and come back later than push on.",
		"Asks other people what they think before deciding anything that cannot be undone.",
	]},
	{max: -.15, notes: [
		"Prefers the route they already know, and takes a new one only under pressure.",
		"Checks the obvious explanation before believing the strange one.",
		"Keeps their opinions to themselves until they know who is listening.",
	]},
	{max: .15, notes: [
		"Takes things as they come, and adjusts rather than objects.",
		"Will try the nearest thing that works, then keep doing it.",
	]},
	{max: .55, notes: [
		"Tries the new thing first and works out the consequences afterwards.",
		"Treats an open door as an invitation.",
		"Uses whatever is to hand, including things that were not offered.",
	]},
	{max: 2, notes: [
		"Would go through a door they do not understand just to find out.",
		"Treats an ability as something to be spent immediately and thoroughly.",
		"Seldom asks whether something should be done; asks how quickly it can be done.",
	]},
] as const;

/** Draws a value uniformly from `[min, max)`, clamped, from the given rng. */
function SampleRange(rng: () => number, min: number, max: number): number {
	const raw = min + rng() * (max - min);
	return Math.max(min, Math.min(max, raw));
}

function PickFrom<T>(items: readonly T[], rng: () => number): T {
	return items[Math.min(items.length - 1, Math.max(0, Math.floor(rng() * items.length)))];
}

/** Generates one new character (§8.2). Deterministic for a given rng, so a seeded run reproduces.
 *
 * As of §8.7 this is the OFFLINE FALLBACK for the character editor's autogen buttons, and the source of
 * the initial draft an empty editor starts from -- not the way a pool entry normally comes into being.
 *
 * The disposition axis is drawn across its whole range on purpose: a pool that clustered at 0 would
 * make every character behave the same way, and §8.2 wants the values to be what carries the reading
 * interest.
 */
export function CreateCharacter(rng: () => number, options?: {name?: string|n, cautionVsOpportunity?: number|n}): EchoCharacter {
	const name = options?.name?.trim() || PickFrom(CHARACTER_NAMES, rng);
	const disposition = options?.cautionVsOpportunity ?? SampleRange(rng, -1, 1);
	const role = PickFrom(CHARACTER_ROLES, rng);

	// one note from each of two bands: one that FOLLOWS from the disposition, one from the trade
	const dispositionBand = CHARACTER_DISPOSITION_NOTES.find(band=>disposition <= band.max) ?? CHARACTER_DISPOSITION_NOTES[CHARACTER_DISPOSITION_NOTES.length - 1];
	const personalityNotes = [
		`${Capitalize(role.role)} by trade; ${role.habit}.`,
		PickFrom(dispositionBand.notes, rng),
	].join(" ");

	return {
		id: GenerateCharacterId(),
		name,
		tagline: `${Capitalize(role.role)} by trade.`,
		values_cautionVsOpportunity: Math.round(disposition * 100) / 100,
		personalityNotes,
		generated: true,
	};
}

/** A random id for a pool character (§8.7). Same rationale as `GenerateWorldId`: a fresh entry has
 * nothing to stay stable against, so unlike the seed worlds it does not need a slug. */
export function GenerateCharacterId(): string {
	return `character_${GenerateSlotId()}`;
}

/** Generates a batch of characters (the rng fallback's "show me a few fresh characters"). */
export function CreateCharacters(count: number, rng: () => number): EchoCharacter[] {
	return [...Array(Math.max(0, count))].map(()=>CreateCharacter(rng));
}

/** Picks the character a NEW story follows (§8.7), optionally excluding the previous story's so two
 * stories in a row do not follow the same person.
 *
 * Falls back to including the excluded character when it is the ONLY one, for the same reason
 * `PickRandomWorld` does: repeating one character is a smaller failure than refusing to start a story.
 * Returns null for an empty pool -- the caller must then tell the user to author one, not invent one. */
export function PickRandomCharacter(pool: EchoCharacter[]|n, rng: () => number, excludeCharacterId?: string|n): EchoCharacter|null {
	const characters = (pool ?? []).filter(a=>a != null);
	if (characters.length == 0) return null;
	if (characters.length == 1) return characters[0];

	const candidates = excludeCharacterId == null ? characters : characters.filter(a=>a.id != excludeCharacterId);
	const usable = candidates.length > 0 ? candidates : characters;
	const index = Math.min(usable.length - 1, Math.max(0, Math.floor(rng() * usable.length)));
	return usable[index];
}

/** Generates an original world (§8.5 `[EXPERIMENT]`), the offline fallback for the Worlds tab's autogen
 * and the same draft an empty world editor starts from (§8.7 has the analogous note for characters).
 *
 * Deliberately conservative: the bible is a handful of CONSTRAINTS (what exists, what is ordinary,
 * what is impossible), never a plot, because a plot summary invites the weaver to write scenes from it
 * instead of from the bullets. A drafted world is always `generated: true`. */
export function CreateWorld(rng: () => number, options?: {name?: string|n}): EchoWorld {
	const name = options?.name?.trim() || PickFrom(WORLD_NAME_PARTS, rng);
	const place = PickFrom(WORLD_PLACE_KINDS, rng);
	const ordinary = PickFrom(WORLD_ORDINARY_DETAILS, rng);
	const impossible = PickFrom(WORLD_IMPOSSIBLE_DETAILS, rng);

	return {
		id: GenerateWorldId(),
		name,
		bible: [
			`${name} is ${place}.`,
			ordinary,
			impossible,
			"IMPORTANT: nobody here finds any of this remarkable; unusual events are handled as ordinary local business and never explained.",
		].join(" "),
		crazinessTolerance: Math.round(SampleRange(rng, .7, .98) * 100) / 100,
		generated: true,
	};
}

/** Name fragments and bible clauses for `CreateWorld`. Kept terse and plain for the same reason
 * `CHARACTER_NAMES` is: an exotic name is a small attention magnet inside prose that should not signal
 * anything. */
const WORLD_NAME_PARTS = [
	"The Quarry", "Bellweather", "Low Tide", "The Long Ward", "Halcyon", "Nine Bridges", "The Foundry",
	"Ferry Road", "Ash Grove", "The Underpass", "Saltmarsh", "The Kiln District", "Glass Harbour",
] as const;

const WORLD_PLACE_KINDS = [
	"a working town built around a single industry that everyone is a little behind on",
	"a coastal settlement where the water comes and goes on a schedule nobody publishes",
	"a district of a much larger city that has been administratively forgotten",
	"an inland town on a road that no longer leads anywhere",
] as const;

const WORLD_ORDINARY_DETAILS = [
	"Work is shift-based, gossip is the main currency, and everyone has a form to file.",
	"People keep animals, argue about parking, and eat the same three meals.",
	"There is a municipal office that answers every question with a different question.",
] as const;

const WORLD_IMPOSSIBLE_DETAILS = [
	"Buildings occasionally rearrange themselves overnight and the corrections are printed in the local paper.",
	"The dead hold down jobs, and their employment disputes are heard like anyone else's.",
	"Anything written down becomes slightly true, so records are kept deliberately vague.",
] as const;

function Capitalize(text: string): string {
	return text.length == 0 ? text : text[0].toUpperCase() + text.substring(1);
}
